import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, TreePine, Train, Building2, Home, Leaf, Tractor, Sprout, FileCheck, MapPin } from "lucide-react";
import { PhotoMarquee } from "../components/site/PhotoMarquee";

const GALLERY = [
  { src: "/api/assets/c0423c5c-00b9-4f34-ac5d-ed5f02ddefd0", alt: "Deer Park entrance sign with flower beds" },
  { src: "/api/assets/cd3cf94f-6d0e-43f1-b117-e4f86eac51de", alt: "Deer Park commercial plaza pylon sign" },
  { src: "/api/assets/b142900c-2d9a-4cd8-9300-2cc3ca6c4084", alt: "Deer Park neighbourhood playground and park sign" },
  { src: "/api/assets/c8cf7c3e-6066-4866-a19a-584539d5fee6", alt: "Deer Park shopping plaza with Tim Hortons and Co-op" },
  { src: "/api/assets/a2de42a4-9c04-4230-9361-a35210a2a6d2", alt: "Community playground on a sunny day" },
  { src: "/api/assets/6b6016cd-b7f3-4fc6-b37c-63f13712730d", alt: "Tree-lined walking trail beside homes" },
  { src: "/api/assets/f5850e96-a4ba-47cc-96b6-ee84de63e037", alt: "Naturalized pond and green space at Deer Park" },
];

const TIMELINE = [
  { year: "1890s", icon: Train, title: "The railway arrives", text: "Settlement in the area grew along the Canadian Northern Railway line west of Edmonton, opening up the parkland prairie for homesteading and farming." },
  { year: "1907", icon: Building2, title: "Spruce Grove becomes a village", text: "Named for the dense stands of spruce that once covered the district, Spruce Grove was incorporated as a village — a small service centre for surrounding farms." },
  { year: "1914", icon: Tractor, title: "A family farm on Section 9", text: "Peter Schultz took ownership of the quarter sections that are now Deer Park Estates — roughly 189 acres of aspen parkland farmed for oats and hay, with a barn and farm buildings on the land." },
  { year: "1955", icon: Home, title: "Growth into a town", text: "Post-war growth along the Yellowhead corridor saw Spruce Grove incorporate as a town, steadily expanding as a bedroom community for the Edmonton region." },
  { year: "1984", icon: Train, title: "The Yellowhead arrives", text: "Highway 16X was built along the north boundary of the land, while both quarter sections stayed in active farming — canola, oats and hay across the neighbouring fields." },
  { year: "1986", icon: Building2, title: "City of Spruce Grove", text: "Spruce Grove achieved city status, becoming one of the fastest-growing communities in the Edmonton Metropolitan Region." },
  { year: "1990", icon: Sprout, title: "The first Deer Park plan", text: "Deer Park Estates Developments Inc. acquired the northwest quarter for subdivision; by 1996 the first Deer Park homes were taking shape on the south and central-east of the land." },
  { year: "1999", icon: FileCheck, title: "Broadview takes the reins", text: "Broadview Developments Inc. acquired the remaining farmland. An independent Phase I environmental assessment by Eco Tech Consulting Ltd. confirmed clean, undisturbed ground — a sound foundation for the community to come." },
  { year: "Today", icon: TreePine, title: "Deer Park Estates takes shape", text: "Broadview master-plans Deer Park Estates — tree-lined streets, two ponds and preserved natural areas, delivered stage by stage." },
];

const ORIGIN_FACTS = [
  { label: "Legal land", value: "SW & NW ¼ Sec 9-53-25-W4", icon: MapPin },
  { label: "Area", value: "≈ 189 acres", icon: TreePine },
  { label: "First farmed", value: "1914 · Peter Schultz", icon: Tractor },
  { label: "Clean assessment", value: "1999 · Eco Tech Consulting", icon: FileCheck },
];

const ARCHIVE = [
  { src: "/api/assets/19a7103a-637a-46eb-86c2-a9aa7e8bc180", year: "1978", caption: "Farmland and wet areas across the two quarter sections — a barn and farm buildings, ringed by trees and bush." },
  { src: "/api/assets/27e7b872-1b53-482c-a78f-5f7f9a0299bc", year: "1984", caption: "Still in active farming, with the new Yellowhead (Hwy 16X) along the north edge and a subdivision emerging to the south-east." },
];

const History = () => {
  return (
    <div className="bg-[#12201A] min-h-screen" data-testid="history-page">
      {/* hero */}
      <section className="relative h-[58vh] min-h-[420px] w-full overflow-hidden">
        <img src="/aerials/trail.jpg" alt="Deer Park natural area" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0C1510]/80 via-[#0C1510]/65 to-[#12201A]" />
        <div className="absolute inset-0 bg-[#0C1510]/35" />
        <div className="relative z-10 h-full max-w-[1100px] mx-auto px-5 md:px-8 flex flex-col justify-end pb-12">
          <p className="eyebrow text-[#C9B79C] mb-3">About Deer Park</p>
          <h1 className="font-display font-light text-4xl md:text-6xl text-[#F3EEE3] leading-[1.03] max-w-3xl">
            Rooted in the spruce grove
          </h1>
          <p className="text-[#F3EEE3]/75 mt-4 max-w-2xl leading-relaxed font-light">
            The story of Deer Park Estates is woven into the history of Spruce Grove — from railway homesteads on the
            parkland prairie to one of the Edmonton region's fastest-growing cities.
          </p>
        </div>
      </section>

      <div className="max-w-[1100px] mx-auto px-5 md:px-8 py-16 space-y-16">
        {/* intro */}
        <section className="max-w-3xl">
          <p className="eyebrow text-[#C9B79C] mb-2">The land</p>
          <h2 className="font-display text-2xl md:text-3xl text-[#F3EEE3]">Parkland, prairie &amp; woodland</h2>
          <p className="text-[#F3EEE3]/70 mt-4 leading-relaxed">
            This corner of central Alberta sits in the aspen parkland — a mosaic of prairie, wetlands and stands of
            spruce and poplar. Long before the town existed, the area was travelled and stewarded by Indigenous peoples
            of the region. It was the spruce forest itself that later gave the community its name, and it is that same
            landscape of trees, water and open sky that Deer Park Estates was designed to preserve.
          </p>
          <div className="mt-6 rounded-2xl border border-[#C9B79C]/25 bg-[#C9B79C]/[0.06] p-6" data-testid="land-acknowledgement">
            <p className="text-[#F3EEE3] font-medium flex items-center gap-2"><Leaf size={16} className="text-[#C9B79C]" /> Whose land this is</p>
            <p className="text-[#F3EEE3]/70 mt-2.5 leading-relaxed text-sm">
              Long before the town or the farm, this is Treaty 6 territory — a traditional gathering place and home
              to the Plains Cree (Nêhiyawak), Nakota (Stoney), Saulteaux and Dene peoples, and part of the homeland
              of the Métis, including the Lac Ste. Anne Métis. We acknowledge their enduring relationship with this
              land, water and sky, and honour that history as the first chapter in the story of Deer Park Estates.
            </p>
          </div>
        </section>

        {/* photo marquee — same running-line style as home */}
        <section>
          <p className="eyebrow text-[#C9B79C] mb-2">In pictures</p>
          <h2 className="font-display text-2xl md:text-3xl text-[#F3EEE3] mb-6">Around Deer Park &amp; Spruce Grove</h2>
          <div className="relative left-1/2 -translate-x-1/2 w-screen">
            <PhotoMarquee photos={GALLERY} />
          </div>
        </section>

        {/* timeline */}
        <section>
          <p className="eyebrow text-[#C9B79C] mb-2">Timeline</p>
          <h2 className="font-display text-2xl md:text-3xl text-[#F3EEE3] mb-8">From homesteads to a modern community</h2>
          <div className="relative border-l border-[#F3EEE3]/15 pl-8 space-y-8">
            {TIMELINE.map((t, i) => {
              const Icon = t.icon;
              return (
                <div key={i} className="relative" data-testid={`history-item-${i}`}>
                  <span className="absolute -left-[41px] top-0 w-6 h-6 rounded-full bg-[#12201A] border border-[#C9B79C]/50 flex items-center justify-center text-[#C9B79C]">
                    <Icon size={12} />
                  </span>
                  <p className="eyebrow text-[0.6rem] text-[#C9B79C]">{t.year}</p>
                  <p className="text-[#F3EEE3] font-medium mt-1 text-lg">{t.title}</p>
                  <p className="text-[#F3EEE3]/60 text-sm mt-1.5 leading-relaxed max-w-2xl">{t.text}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* land provenance / origins */}
        <section data-testid="land-origins">
          <p className="eyebrow text-[#C9B79C] mb-2">Before the estate</p>
          <h2 className="font-display text-2xl md:text-3xl text-[#F3EEE3] mb-4">A century of parkland farming</h2>
          <p className="text-[#F3EEE3]/70 leading-relaxed max-w-3xl">
            For most of the last century the land that became Deer Park Estates was a working farm on the
            south-west and north-west quarters of Section 9-53-25-W4, at the edge of Spruce Grove. Aerial
            photographs from 1978 show it as a broad holding of aspen parkland — oats and hay, a barn and farm
            buildings, ringed by trees, bush and quiet wet areas, with the Yellowhead built along its northern
            edge in 1984. When Broadview Developments Inc. acquired the property in 1999, an independent Phase I
            environmental assessment by Eco Tech Consulting Ltd. found clean, undisturbed ground with no sign of
            contamination — the healthy foundation the community was built on.
          </p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-7">
            {ORIGIN_FACTS.map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={i} data-testid={`origin-fact-${i}`} className="rounded-2xl border border-[#F3EEE3]/12 bg-[#0F1B15] p-5">
                  <Icon size={18} className="text-[#C9B79C]" />
                  <p className="eyebrow text-[0.55rem] text-[#C9B79C] mt-3">{f.label}</p>
                  <p className="text-[#F3EEE3] text-sm mt-1 font-medium leading-snug">{f.value}</p>
                </div>
              );
            })}
          </div>
          <div className="grid sm:grid-cols-2 gap-6 mt-8">
            {ARCHIVE.map((a, i) => (
              <figure key={i} data-testid={`aerial-${a.year}`} className="rounded-2xl overflow-hidden border border-[#F3EEE3]/12 bg-[#0F1B15]">
                <div className="relative">
                  <img src={a.src} alt={`Aerial photograph of the Deer Park Estates land in ${a.year}`} className="w-full h-auto object-cover grayscale-[0.15]" />
                  <span className="absolute bottom-3 left-3 text-[0.7rem] tracking-widest uppercase px-3 py-1 rounded-full bg-[#0C1510]/80 text-[#C9B79C] backdrop-blur-sm">{a.year} · aerial</span>
                </div>
                <figcaption className="text-[#F3EEE3]/60 text-xs leading-relaxed p-4">{a.caption}</figcaption>
              </figure>
            ))}
          </div>
          <p className="text-[#F3EEE3]/40 text-[0.7rem] mt-3">Historical aerial photographs from the 1999 Phase I Environmental Assessment by Eco Tech Consulting Ltd.</p>
        </section>

        {/* name story */}
        <section className="rounded-2xl border border-[#F3EEE3]/12 bg-[#0F1B15] p-8 md:p-10">
          <p className="text-[#F3EEE3] font-medium flex items-center gap-2 text-lg"><Leaf size={18} className="text-[#C9B79C]" /> Why "Deer Park"?</p>
          <p className="text-[#F3EEE3]/65 mt-3 leading-relaxed max-w-3xl">
            The name reflects the setting the community was built around — quiet woodland edges, ponds and green corridors
            where deer and other wildlife move through. Rather than clearing the land flat, the master plan keeps
            preserved natural areas, two ponds and a trail network at its centre, so the "park" is woven right into
            everyday life for residents.
          </p>
        </section>

        {/* CTA */}
        <section className="text-center">
          <h3 className="font-display font-light text-2xl md:text-3xl text-[#F3EEE3]">Explore the community today</h3>
          <div className="mt-7 flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/community" className="inline-flex items-center justify-center gap-2 bg-[#F3EEE3] text-[#12201A] px-8 py-4 rounded-full font-medium hover:bg-white transition-colors">
              Life around Deer Park <ArrowRight size={16} />
            </Link>
            <Link to="/location" className="inline-flex items-center justify-center gap-2 border border-[#F3EEE3]/40 text-[#F3EEE3] px-8 py-4 rounded-full font-medium hover:bg-[#F3EEE3]/10 transition-colors">
              Open the area map
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
};

export default History;
